import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useMemo, useEffect } from "react";
import { Building2, MapPin, Users, ArrowRight, Search, BadgeCheck } from "lucide-react";
import { listAgenciasPublic } from "@/lib/agencias.functions";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { buildSeo, SEO_ORIGIN } from "@/lib/seo";
import { searchScore } from "@/lib/search-text";

/** Agencias que se pintan de cada vez en el directorio. */
const POR_PAGINA = 60;

type AgenciaPublicCard = {
  id: string;
  slug: string | null;
  nombre: string;
  logo_url: string | null;
  region: string | null;
  num_empleados: number | null;
  descripcion_publica: string | null;
  estado: "activa" | "inactiva" | "bloqueada" | null;
};

export const Route = createFileRoute("/agencia/")({
  loader: async () => {
    try {
      const rows = (await listAgenciasPublic()) as AgenciaPublicCard[];
      return { agencias: rows ?? [] };
    } catch {
      return { agencias: [] as AgenciaPublicCard[] };
    }
  },
  head: ({ loaderData }) => {
    const title = "Agencias inmobiliarias especializadas en locales y traspasos | Bizaclia";
    const description =
      "Directorio de agencias inmobiliarias especializadas en locales comerciales, oficinas, naves y traspasos de negocios en España. Encuentra tu agencia en Bizaclia.";

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: `${SEO_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Agencias", item: `${SEO_ORIGIN}/agencia` },
      ],
    };

    const items = (loaderData?.agencias ?? []).slice(0, 30);
    const itemList = items.length
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: items.map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SEO_ORIGIN}/agencia/${a.id}`,
            name: a.nombre,
          })),
        }
      : null;

    return buildSeo({
      title,
      description,
      path: "/agencia",
      jsonLd: itemList ? [breadcrumb, itemList] : [breadcrumb],
    });
  },
  component: AgenciasIndexPage,
});

function AgenciasIndexPage() {
  const { agencias: initial } = Route.useLoaderData();
  const [search, setSearch] = useState("");
  const list = useServerFn(listAgenciasPublic);
  const q = useQuery<AgenciaPublicCard[]>({
    queryKey: ["agencias-public"],
    queryFn: async () => (await list()) as AgenciaPublicCard[],
    initialData: initial as AgenciaPublicCard[],
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return q.data ?? [];
    return (q.data ?? []).filter((a) => {
      const haystack = [a.nombre, a.slug, a.region].filter(Boolean).join(" ");
      return searchScore(haystack, search) >= 0;
    });
  }, [q.data, search]);

  // Hay miles de agencias: se pintan por tandas para no bloquear la página.
  const [visibles, setVisibles] = useState(POR_PAGINA);
  useEffect(() => setVisibles(POR_PAGINA), [search]);
  const mostradas = filtered.slice(0, visibles);
  const quedan = filtered.length - mostradas.length;

  return (
    <main className="container mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
      <header className="mb-8 sm:mb-10">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
          Directorio
        </span>
        <h1 className="mt-3 font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight">
          Agencias inmobiliarias especializadas en negocios
        </h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">
          Explora agencias inmobiliarias verificadas, especializadas en traspasos, alquiler y venta
          de locales comerciales, oficinas y naves en toda España. Descubre sus anuncios activos y
          contacta directamente desde Bizaclia.
        </p>
      </header>

      <div className="mb-6 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            placeholder="Buscar por nombre, slug o región"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {q.isLoading ? (
        <p className="text-muted-foreground">Cargando agencias…</p>
      ) : q.error ? (
        <p className="text-destructive">Error al cargar las agencias.</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground">
          {search.trim()
            ? "No se encontraron agencias con ese criterio."
            : "Aún no hay agencias publicadas."}
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {mostradas.map((a) => (
            <Link key={a.id} to="/agencia/$id" params={{ id: a.id }} className="group">
              <Card className="h-full transition-shadow hover:shadow-md">
                <CardContent className="p-5 flex gap-4">
                  <div className="h-16 w-16 shrink-0 rounded-lg bg-muted overflow-hidden flex items-center justify-center">
                    {a.logo_url ? (
                      <img
                        src={a.logo_url}
                        alt={`Logo de ${a.nombre}`}
                        loading="lazy"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <Building2 className="h-7 w-7 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold text-lg leading-tight break-words group-hover:text-primary transition-colors">
                        {a.nombre}
                      </h2>
                      {a.estado === "activa" && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                          <BadgeCheck className="h-3.5 w-3.5" />
                          Verificada
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      {a.region && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {a.region}
                        </span>
                      )}
                      {a.num_empleados != null && (
                        <span className="inline-flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {a.num_empleados}
                        </span>
                      )}
                    </div>
                    {a.descripcion_publica && (
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                        {a.descripcion_publica}
                      </p>
                    )}
                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary">
                      Ver agencia <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
      {quedan > 0 && (
        <div className="mt-8 flex flex-col items-center gap-2">
          <Button variant="outline" onClick={() => setVisibles((v) => v + POR_PAGINA)}>
            Mostrar más agencias
          </Button>
          <span className="text-xs text-muted-foreground">
            {mostradas.length} de {filtered.length}
          </span>
        </div>
      )}
    </main>
  );
}
